import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import RTSpinner from '../common/RTSpinner';
import RTButton from '../common/RTButton';
import RTPageLayout from '../common/RTPageLayout';
import { apiGet } from '../../api/apiClient';
import { toastError } from '../common/toast';
import { formatDateTimeToDDMMYYYY } from '../../utils/dateHelper';
import { hasPermission } from '../../utils/permissions';
import { prettyStatus, statusBadgeClass } from './pgFormat';
import 'bootstrap/dist/css/bootstrap.min.css';
import './PGPortal.css';

const OPEN_STATUSES = new Set(['open', 'assigned', 'in_progress']);
const RESOLVED_STATUSES = new Set(['resolved', 'closed']);

const PGComplaints = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('open');
  const [openComplaints, setOpenComplaints] = useState([]);
  const [resolvedComplaints, setResolvedComplaints] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const listRes = await apiGet('/pg/me/complaints');
      const all = listRes.data || [];
      setOpenComplaints(all.filter((c) => OPEN_STATUSES.has(c.status)));
      setResolvedComplaints(all.filter((c) => RESOLVED_STATUSES.has(c.status)));
    } catch (err) {
      toastError(err.response?.data?.detail || 'Unable to load complaints.');
      setOpenComplaints([]);
      setResolvedComplaints([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const renderList = (items, emptyMessage) => {
    if (loading) {
      return <RTSpinner message="Loading complaints…" fullScreen={false} />;
    }
    if (items.length === 0) {
      return <div className="pg-empty">{emptyMessage}</div>;
    }
    return items.map((c) => (
      <div
        key={c.complaint_id}
        className="pg-card pg-list-item"
        onClick={() => navigate(`/pg/complaints/view/${c.complaint_id}`)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter')
            navigate(`/pg/complaints/view/${c.complaint_id}`);
        }}
      >
        <div className="pg-card-title">
          <h3>{c.complaint_type}</h3>
          <span className={`pg-badge ${statusBadgeClass(c.status)}`}>
            {prettyStatus(c.status)}
          </span>
        </div>
        <p style={{ margin: '0 0 8px', fontSize: '0.92rem', color: '#333' }}>
          {c.description}
        </p>
        <div className="pg-muted">
          Raised {formatDateTimeToDDMMYYYY(c.created_at)}
        </div>
        {c.resolution_notes ? (
          <div
            className="pg-alert pg-alert-info"
            style={{ marginTop: 10, marginBottom: 0 }}
          >
            {c.resolution_notes}
          </div>
        ) : null}
      </div>
    ));
  };

  return (
    <RTPageLayout
      title="Complaints"
      subtitle="Raise and track your requests"
      mainSection
      actions={
        hasPermission('pg-complaints.add') ? (
          <RTButton
            variant="primary"
            onClick={() => navigate('/pg/complaints/add')}
          >
            Raise complaint
          </RTButton>
        ) : null
      }
    >
      <div className="pg-portal">
        <Tabs
          activeKey={activeTab}
          onSelect={(tab) => tab && setActiveTab(tab)}
          id="pg-complaints-tabs"
          className="pg-complaints-tabs"
          justify
        >
          <Tab eventKey="open" title={`Open (${openComplaints.length})`}>
            <div className="pg-complaints-tab-body">
              {renderList(openComplaints, 'No open complaints right now.')}
            </div>
          </Tab>
          <Tab
            eventKey="resolved"
            title={`Resolved (${resolvedComplaints.length})`}
          >
            <div className="pg-complaints-tab-body">
              {renderList(resolvedComplaints, 'No resolved complaints yet.')}
            </div>
          </Tab>
        </Tabs>
      </div>
    </RTPageLayout>
  );
};

export default PGComplaints;
